<template>
  <v-container grid-list-lg fluid>
    <TitleBar title="Role" />

    <v-row>
      <v-col cols="12">
        <v-card outlined class="rounded-xl">
          <CardToolbar title="Edit Role" :isModal="false" />

          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="2">
                <span class="font-weight-medium">Name</span>
              </v-col>

              <v-col cols="12" sm="8">
                <v-text-field
                  outlined
                  v-model="role.name"
                  label="Name"
                  hint="e.g. Sales Manager, Junior, Read Only"
                  @input="$v.role.name.$touch()"
                  :error-messages="nameErrors"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      

      <!-- PERMISSIONS LIST -->
      <v-col cols="12">
        <v-card flat class="outlined rounded-xl">
          <v-card-text>
            <v-text-field
              v-model="search"
              label="Search permissions"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              class="mb-4"
            />

            <div v-for="groupName in filteredGroups" :key="groupName">
              <h3 class="subtitle-2 font-weight-bold mb-2">
                {{ groupName }}
              </h3>

              <v-divider class="mb-2" />

              <v-row
                v-for="permission in filteredPermissions[groupName]"
                :key="permission.id"
                align="center"
                class="py-2"
              >
                <v-col cols="12" md="9" lg="10">
                 
                  
                  <span
                    class="font-weight-medium"
                    v-html="highlight(permission.name)"
                  >
                  </span>

                  <p
                    class="font-weight-light mb-0"
                    v-html="highlight(permission.description)"
                  ></p>
                </v-col>

                <v-col cols="12" md="3" lg="2">
                  <v-row align="center" no-gutters>
                    <span class="mr-2">OFF</span>

                    <v-switch
                      v-model="permission.allow"
                      color="primary"  class="ma-0"
                      hide-details
                      @change="switchChanged(permission.stringId)"
                    />

                    <span class="ml-2">ON</span>
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>

        
      </v-col>
    </v-row>

    <!-- ACTIONS -->
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text class="text-capitalize" @click="cancel">Cancel</v-btn>
      <v-btn dark class="v-btn-with-radius" @click="submit">Update</v-btn>
    </v-card-actions>
  </v-container>
</template>

<script>
import _ from "lodash";
import roleService from "../service";
import employeeService from "../../../service.js";
import Loading from "../../../../../components/LoadingDialog";
const { validationMixin } = require("vuelidate");
const { required, minLength } = require("vuelidate/lib/validators");

export default {
  name: "role-edit",
  mixins: [validationMixin],

  data() {
    return {
      search: "",
      newPermissions: {},
      permissions: [],
      limitedHistoryEnabled: true,
      historyDays: 1,
      role: {
        id: null,
        name: null,
      },
      isLoading: false,
    };
  },

  async created() {
    await this.loadPermissions();
    await this.loadRole();
  },

  computed: {
    nameErrors() {
      const e = [];
      if (!this.$v.role.name.$dirty) return e;
      if (!this.$v.role.name.required) e.push("Name is required.");
      if (!this.$v.role.name.minLength)
        e.push("Must be at least 4 characters.");
      return e;
    },

    filteredGroups() {
      const q = (this.search || "").toLowerCase().trim();

      return Object.keys(this.filteredPermissions).filter(
        (group) => this.filteredPermissions[group].length > 0
      );
    },

    filteredPermissions() {
      const q = (this.search || "").toLowerCase().trim();
      const result = {};

      Object.keys(this.newPermissions).forEach((group) => {
        const items = this.newPermissions[group];

        // filter by name or description
        const filtered = items.filter((p) => {
          return (
            p.name.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q)
          );
        });

        if (filtered.length) result[group] = filtered;
      });

      return result;
    },
  },

  methods: {
    /** Load all permissions */
    async loadPermissions() {
      this.isLoading = true;

      const response = await employeeService.getTerminalPermissions();
      this.permissions = response.data;

      this.permissions.forEach((p) => {
        p.allow = p.defaultPermission;

        if (p.stringId === "HISTORYLIMITEDACCESS" && !p.metadata) {
          p.metadata = { days: 1 };
          this.limitedHistoryEnabled = p.defaultPermission;
        }
      });

      this.newPermissions = _.mapValues(
        _.groupBy(this.permissions, (p) => p.TPermissionGroup.name),
        (list) => list
      );

      this.isLoading = false;
    },

    /** Load role settings */
    async loadRole() {
      const { id } = this.$route.params;
      if (!id) return this.$router.push("/employee/role");

      const response = await roleService.getById(id);
      this.role.id = response.data.id;
      this.role.name = response.data.name;

      response.data.EmployeeRolePermissions.forEach((rp) => {
        const idx = this.permissions.findIndex(
          (p) => p.id === rp.TerminalPermissionId
        );
        if (idx >= 0) {
          this.permissions[idx].allow = rp.allow;
          if (rp.metadata) this.permissions[idx].metadata = rp.metadata;
        }
      });
    },

    /** Highlight search */
    highlight(text) {
      if (!this.search) return text;
      const re = new RegExp(this.search, "gi");
      return text.replace(re, (match) => `<mark>${match}</mark>`);
    },

    switchChanged(id) {
      if (id === "HISTORYLIMITEDACCESS") {
        this.limitedHistoryEnabled = !this.limitedHistoryEnabled;
      }
    },

    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.role.Permissions = this.permissions;

      await roleService.update(this.role.id, this.role);

      this.$swal.fire({
        toast: true,
        icon: "success",
        title: "Role updated",
        timer: 3000,
        position: "top-end",
        showConfirmButton: false,
      });

      this.$router.go(-1);
    },

    cancel() {
      this.$router.go(-1);
    },
  },

  validations: {
    role: {
      name: { required, minLength: minLength(4) },
    },
  },
};
</script>